import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@src/environments/environment';
import { GenericResponse } from '@app/models/backend';
import {
  AsistenciaDTO,
  AsistenciaCreateDTO,
  AsistenciaFilterDTO,
} from '@app/models/backend/asistencia';

@Injectable({
  providedIn: 'root',
})
export class AsistenciaService {
  private asistenciaUrl = environment.url + 'api/asistencias';

  constructor(private http: HttpClient) {}

  /**
   * Registra asistencia manual
   */
  registrarAsistencia(
    asistencia: AsistenciaCreateDTO
  ): Observable<GenericResponse<AsistenciaDTO>> {
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}`,
      asistencia
    );
  }

  /**
   * Registra entrada automática
   */
  registrarEntrada(
    usuarioId: number
  ): Observable<GenericResponse<AsistenciaDTO>> {
    const body = { 
      usuarioId: usuarioId
    };
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}`,
      body
    );
  }

  /**
   * 🔥 NUEVO: Registra asistencia desde el header/menú desplegable
   */
  registrarAsistenciaHeader(
    usuarioId: number
  ): Observable<GenericResponse<AsistenciaDTO>> {
    const body = { 
      usuarioId: usuarioId
    };
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}`,
      body
    );
  }

  /**
   * 🔥 CORREGIDO: Registra salida
   */
  registrarSalida(
    usuarioId: number
  ): Observable<GenericResponse<AsistenciaDTO>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}/salida`,
      null,
      { params }
    );
  }

  /**
   * 🔥 CORREGIDO: Inicia break
   */
  iniciarBreak(usuarioId: number): Observable<GenericResponse<AsistenciaDTO>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}/break/iniciar`,
      null,
      { params }
    );
  }

  /**
   * 🔥 CORREGIDO: Finaliza break
   */
  finalizarBreak(
    usuarioId: number
  ): Observable<GenericResponse<AsistenciaDTO>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}/break/finalizar`,
      null,
      { params }
    );
  }

  /**
   * 🔥 CORREGIDO: Inicia ida al baño
   */
  iniciarBano(usuarioId: number): Observable<GenericResponse<AsistenciaDTO>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}/bano/iniciar`,
      null,
      { params }
    );
  }

  /**
   * 🔥 CORREGIDO: Finaliza ida al baño
   */
  finalizarBano(usuarioId: number): Observable<GenericResponse<AsistenciaDTO>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}/bano/finalizar`,
      null,
      { params }
    );
  }

  /**
   * 🔥 NUEVO: Inicia break optimizado para header (solo contadores)
   */
  iniciarBreakHeader(usuarioId: number): Observable<GenericResponse<any>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<any>>(
      `${this.asistenciaUrl}/break/iniciar-header`,
      null,
      { params }
    );
  }

  /**
   * 🔥 NUEVO: Finaliza break optimizado para header (solo contadores)
   */
  finalizarBreakHeader(usuarioId: number): Observable<GenericResponse<any>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<any>>(
      `${this.asistenciaUrl}/break/finalizar-header`,
      null,
      { params }
    );
  }

  /**
   * 🔥 NUEVO: Inicia baño optimizado para header (solo contadores)
   */
  iniciarBanoHeader(usuarioId: number): Observable<GenericResponse<any>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<any>>(
      `${this.asistenciaUrl}/bano/iniciar-header`,
      null,
      { params }
    );
  }

  /**
   * 🔥 NUEVO: Finaliza baño optimizado para header (solo contadores)
   */
  finalizarBanoHeader(usuarioId: number): Observable<GenericResponse<any>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<any>>(
      `${this.asistenciaUrl}/bano/finalizar-header`,
      null,
      { params }
    );
  }

  /**
   * 🔥 NUEVO: Obtiene asistencias optimizadas para asesor (solo campos necesarios)
   */
  obtenerAsistenciasAsesorOptimizado(
    usuarioId: number,
    fechaInicio: string,
    fechaFin: string
  ): Observable<GenericResponse<any[]>> {
    const params = new HttpParams()
      .set('usuarioId', usuarioId.toString())
      .set('fechaInicio', fechaInicio)
      .set('fechaFin', fechaFin);
    return this.http.get<GenericResponse<any[]>>(
      `${this.asistenciaUrl}/asesor/rango-optimizado`,
      { params }
    );
  }

  /**
   * 🔥 NUEVO: Obtiene estado actual de actividades
   */
  obtenerEstadoActividades(
    usuarioId: number
  ): Observable<GenericResponse<any>> {
    return this.http.get<GenericResponse<any>>(
      `${this.asistenciaUrl}/actividad-activa/${usuarioId}`
    );
  }

  /**
   * 🔥 NUEVO: Auto-finaliza actividades por tiempo límite
   */
  autoFinalizarActividades(
    usuarioId: number
  ): Observable<GenericResponse<AsistenciaDTO[]>> {
    return this.http.post<GenericResponse<AsistenciaDTO[]>>(
      `${this.asistenciaUrl}/auto-finalizar/${usuarioId}`,
      null
    );
  }

  /**
   * Inicia sesión CRM
   */
  iniciarSesionCrm(
    usuarioId: number
  ): Observable<GenericResponse<AsistenciaDTO>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}/sesion-crm/iniciar`,
      null,
      { params }
    );
  }

  /**
   * Finaliza sesión CRM
   */
  finalizarSesionCrm(
    usuarioId: number
  ): Observable<GenericResponse<AsistenciaDTO>> {
    const params = new HttpParams().set('usuarioId', usuarioId.toString());
    return this.http.post<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}/sesion-crm/finalizar`,
      null,
      { params }
    );
  }

  /**
   * 🔥 CORREGIDO: Obtiene asistencias con filtros
   */
  obtenerAsistencias(
    filtros: AsistenciaFilterDTO
  ): Observable<GenericResponse<any>> {
    return this.http.post<GenericResponse<any>>(
      `${this.asistenciaUrl}/filtros`,
      filtros
    );
  }

  /**
   * 🔥 CORREGIDO: Obtiene asistencias por fecha específica
   */
  obtenerAsistenciasPorFecha(
    fecha: string
  ): Observable<GenericResponse<AsistenciaDTO[]>> {
    const params = new HttpParams().set('fecha', fecha);
    return this.http.get<GenericResponse<AsistenciaDTO[]>>(
      `${this.asistenciaUrl}/fecha`,
      { params }
    );
  }

  /**
   * 🔥 CORREGIDO: Obtiene asistencias de un usuario específico
   */
  obtenerAsistenciasPorUsuario(
    usuarioId: number
  ): Observable<GenericResponse<AsistenciaDTO[]>> {
    return this.http.get<GenericResponse<AsistenciaDTO[]>>(
      `${this.asistenciaUrl}/usuario/${usuarioId}`
    );
  }

  /**
   * 🔥 CORREGIDO: Obtiene asistencias de un usuario en un rango de fechas
   */
  obtenerAsistenciasUsuarioRango(
    usuarioId: number,
    fechaInicio: string,
    fechaFin: string
  ): Observable<GenericResponse<AsistenciaDTO[]>> {
    const params = new HttpParams()
      .set('usuarioId', usuarioId.toString())
      .set('fechaInicio', fechaInicio)
      .set('fechaFin', fechaFin);
    return this.http.get<GenericResponse<AsistenciaDTO[]>>(
      `${this.asistenciaUrl}/usuario/rango`,
      { params }
    );
  }

  /**
   * Obtiene asistencia por ID
   */
  obtenerAsistenciaPorId(
    id: number
  ): Observable<GenericResponse<AsistenciaDTO>> {
    return this.http.get<GenericResponse<AsistenciaDTO>>(
      `${this.asistenciaUrl}/${id}`
    );
  }

  /**
   * Verifica si el usuario puede tomar break
   */
  puedeTomarBreak(usuarioId: number): Observable<GenericResponse<boolean>> {
    return this.http.get<GenericResponse<boolean>>(
      `${this.asistenciaUrl}/break/puede-tomar/${usuarioId}`
    );
  }

  /**
   * Verifica si el usuario puede ir al baño
   */
  puedeIrAlBano(usuarioId: number): Observable<GenericResponse<boolean>> {
    return this.http.get<GenericResponse<boolean>>(
      `${this.asistenciaUrl}/bano/puede-ir/${usuarioId}`
    );
  }

  /**
   * 🔥 CORREGIDO: Verifica si un usuario ya registró asistencia hoy
   */
  yaRegistroAsistenciaHoy(
    usuarioId: number
  ): Observable<GenericResponse<boolean>> {
    return this.http.get<GenericResponse<boolean>>(
      `${this.asistenciaUrl}/verificar-hoy/${usuarioId}`
    );
  }

  /**
   * 🔥 CORREGIDO: Obtiene estadísticas de asistencia
   */
  obtenerEstadisticas(
    fechaInicio: string,
    fechaFin: string
  ): Observable<GenericResponse<any>> {
    const params = new HttpParams()
      .set('fechaInicio', fechaInicio)
      .set('fechaFin', fechaFin);
    return this.http.get<GenericResponse<any>>(
      `${this.asistenciaUrl}/estadisticas`,
      { params }
    );
  }
}
